// apps/web/src/app/shell/PindahHalaman.tsx
/**
 * Moving to another page, for a person who does not see the screen change (an accessibility
 * follow-up of the sweep):
 *
 * - The focus goes to the new page's main heading, its h1 (made focusable with tabIndex -1;
 *   never the print sheet's own heading on /cetak), as soon as it is on screen: at once, when
 *   the old page has gone (a route transition keeps it a moment), when a dialog that was open
 *   at the move has closed (Tinggalkan in the leave question), or when the page's data
 *   arrives. Never when the page has put the focus itself (a wizard's step heading, an edit
 *   form's field, a dialog it opened after the move), when the person has moved on first (a
 *   key, a pointer), on a change of the address's query alone (filters, `?ubah`), or on the
 *   first page of a visit (the skip link is there). Each look waits a turn of the event loop,
 *   so a page's own focus effects come first and the focus never moves twice.
 * - A sentence handed to the next page (`{ umumkan: <key> }`, `app/serahan.ts`: fixed words,
 *   never anything typed) is read out once in the shell's polite live region: the region is
 *   always on the page, so a screen reader hears it change, which a status line the new page
 *   mounts with its text already in it is not. Back, Forward and a reload (the history keeps
 *   the state) say nothing again. A screen may also ask for one through `useUmumkan` (the bulk
 *   selection's count); it is read the same way.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigationType } from 'react-router'
import { kalimatUmumkan, type SerahanHalaman } from '../serahan'
import { useUmumkan } from '../store/umumkan'

/** How long a page may take to show its heading (a slow first read). */
const TUNGGU_JUDUL_MS = 5000
/** The live region empties first, so the same sentence twice is still heard twice. */
const JEDA_UMUMKAN_MS = 100
const DIALOG = '[role="dialog"], [role="alertdialog"], [aria-modal="true"]'

/** The page's main heading: the newest h1 of the content (the new page's while the old one is
 *  still there), never a print sheet's own. */
function judulHalaman(isi: HTMLElement): HTMLElement | undefined {
  return [...isi.querySelectorAll('h1')].filter((h) => h.closest('.sheet-stage') === null).at(-1)
}

export function PindahHalaman({ isiId = 'isi' }: { isiId?: string }) {
  const lokasi = useLocation()
  const jenis = useNavigationType()
  const halaman = useRef(lokasi.pathname)
  const [umumkan, setUmumkan] = useState('')

  useEffect(() => {
    // Only another page: not the first of the visit (the same path), not a change of the query.
    const halamanBaru = lokasi.pathname !== halaman.current
    halaman.current = lokasi.pathname
    if (!halamanBaru) return
    const isi = document.getElementById(isiId)
    if (isi === null) return
    // The dialogs open at the move (the leave question just answered) are closing: a focus
    // in one of them is waited out, not taken as the page's own.
    const dialogAwal = new Set(document.querySelectorAll(DIALOG))
    let selesai = false
    let jamCoba: ReturnType<typeof setTimeout> | null = null
    // Watched until the person acts or the time is up: during the route's transition the old
    // page is still there (its clicked link may still hold the focus, its h1 may still show),
    // and it goes a moment later.
    const coba = () => {
      if (selesai) return
      const aktif = document.activeElement
      const dialog = aktif?.closest(DIALOG) ?? null
      if (dialog !== null) {
        // A dialog the new page opened (the checking screen of `?baca`) holds it: done.
        if (!dialogAwal.has(dialog)) berhenti()
        return
      }
      // A live element of the content holds the focus (the new page put it there, or the old
      // page has not gone yet): wait. The focus falling to the body or the content box (the
      // old page went, a dialog closed), or one left outside it (a sidebar link), goes to the
      // heading.
      if (aktif !== null && aktif !== isi && aktif !== document.body && isi.contains(aktif)) {
        return
      }
      const judul = judulHalaman(isi)
      if (judul === undefined) return
      if (!judul.hasAttribute('tabindex')) judul.tabIndex = -1
      judul.focus({ preventScroll: true })
    }
    // A turn of the event loop first: the page's own focus effects (a field, its heading) win.
    const jadwal = () => {
      if (selesai || jamCoba !== null) return
      jamCoba = setTimeout(() => {
        jamCoba = null
        coba()
      }, 0)
    }
    // The whole body: the content changing, and a dialog's portal going away.
    const pengamat = new MutationObserver(jadwal)
    const olehOrang = () => berhenti()
    const jam = setTimeout(() => berhenti(), TUNGGU_JUDUL_MS)
    function berhenti() {
      selesai = true
      pengamat.disconnect()
      clearTimeout(jam)
      if (jamCoba !== null) clearTimeout(jamCoba)
      window.removeEventListener('keydown', olehOrang, true)
      window.removeEventListener('pointerdown', olehOrang, true)
    }
    pengamat.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('keydown', olehOrang, true)
    window.addEventListener('pointerdown', olehOrang, true)
    jadwal()
    return berhenti
  }, [lokasi.pathname, isiId])

  useEffect(() => {
    setUmumkan('')
    // Back, Forward, a reload and the first page of a visit: told before, not again.
    if (jenis === 'POP') return
    const pesan = kalimatUmumkan((lokasi.state as SerahanHalaman | null)?.umumkan)
    if (pesan === null) return
    const jam = setTimeout(() => setUmumkan(pesan), JEDA_UMUMKAN_MS)
    return () => clearTimeout(jam)
  }, [lokasi.state, jenis])

  // A sentence a screen asks for (the bulk selection's count): read out the same way, the region
  // emptied first. The value at mount is an earlier screen's: never read again.
  const nomorUmumkan = useUmumkan((s) => s.nomor)
  const nomorAwal = useRef(nomorUmumkan)
  useEffect(() => {
    if (nomorUmumkan === nomorAwal.current) return
    setUmumkan('')
    const jam = setTimeout(() => setUmumkan(useUmumkan.getState().kalimat), JEDA_UMUMKAN_MS)
    return () => clearTimeout(jam)
  }, [nomorUmumkan])

  return (
    <p data-pengumuman="" role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {umumkan}
    </p>
  )
}
